import { FC, useState, Fragment, useEffect } from 'react'
import { SelectionField } from '../../../../generated'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { Box, Typography, Stack, TextField, Dialog, DialogContent, DialogActions, Button } from '@mui/material'
import DateRangeFilter from '../../common/DateRangeFilter'
import { getDateWithTz } from '../../../utils'

interface DateRangePickerProps {
  field: SelectionField
  reportFilters: Record<string, any>
  setReportFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

const DateRangePicker: FC<DateRangePickerProps> = ({ field, reportFilters, setReportFilters }) => {
  const minDate = field.minDate ?? undefined
  // Keep the default/initial "from" at or after the absolute floor (e.g. General Ledger's Jan 1, 2026).
  const clampFrom = (d: dayjs.Dayjs) => (minDate && d.isBefore(dayjs(minDate)) ? dayjs(minDate) : d)

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [tempDateRange, setTempDateRange] = useState({
    from: clampFrom(field.defaultValue?.from ? dayjs(field.defaultValue.from) : dayjs().subtract(1, 'month')),
    to: field.defaultValue?.to ? dayjs(field.defaultValue.to) : dayjs()
  })
  const fieldName = field.field
  const handleDateRangeConfirm = () => {
    setReportFilters(prev => ({
      ...prev,
      [fieldName]: {
        start: getDateWithTz(tempDateRange?.from),
        end: getDateWithTz(tempDateRange?.to?.endOf('day'))
      }
    }))
    setIsDatePickerOpen(false)
  }

  // Whether the parent currently holds a range for this field. Switching reports
  // clears reportFilters, and because the parent's reset effect runs AFTER this
  // child's effects, a mount-only seed would be wiped straight afterwards. Making
  // this a dependency re-seeds whenever the value goes missing, so the range the
  // user sees is always the range that will actually be sent.
  const hasRange = Boolean(reportFilters?.[fieldName]?.start && reportFilters?.[fieldName]?.end)

  useEffect(() => {
    const newFrom = clampFrom(field.defaultValue?.from ? dayjs(field.defaultValue.from) : dayjs().subtract(1, 'month'))
    const newTo = field.defaultValue?.to ? dayjs(field.defaultValue.to) : dayjs()
    setTempDateRange({ from: newFrom, to: newTo })
    setReportFilters(prev => ({
      ...prev,
      [fieldName]: {
        start: newFrom.format('YYYY-MM-DD'),
        end: newTo.endOf('day').format('YYYY-MM-DD HH:mm:ss')
      }
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fieldName,
    hasRange,
    dayjs(field.defaultValue?.from).format('YYYY-MM-DD'),
    dayjs(field.defaultValue?.to).format('YYYY-MM-DD')
  ])

  if (field?.type !== 'DATE_RANGE') return null

  return (
    <Fragment>
      <Stack direction="row" spacing={2} sx={{ minWidth: 320 }}>
        <Box width="100%">
          <TextField
            fullWidth
            label={field?.label || 'Date Range'}
            value={
              reportFilters?.[fieldName]?.start && reportFilters?.[fieldName]?.end
                ? `${dayjs(reportFilters[fieldName].start).format('MM/DD/YYYY')} - ${dayjs(
                    reportFilters[fieldName].end
                  ).format('MM/DD/YYYY')}`
                : `${tempDateRange.from.format('MM/DD/YYYY')} - ${tempDateRange.to.format('MM/DD/YYYY')}`
            }
            InputProps={{
              endAdornment: (
                <Box component="span" sx={{ cursor: 'pointer', ml: 4 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 2V6M8 2V6M3 10H21"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              )
            }}
            onClick={() => setIsDatePickerOpen(true)}
            sx={{
              '& .MuiOutlinedInput-root': {
                cursor: 'pointer',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              },
              '& .MuiInputBase-input': {
                cursor: 'pointer'
              }
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }} />
      </Stack>

      <Dialog open={isDatePickerOpen} onClose={() => setIsDatePickerOpen(false)} maxWidth="xs">
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Date Range
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={2}>
              <DateRangeFilter
                fromDate={tempDateRange.from.format('MM/DD/YYYY')}
                toDate={tempDateRange.to.format('MM/DD/YYYY')}
                onFromDateChange={value => value && setTempDateRange(prev => ({ ...prev, from: dayjs(value) }))}
                onToDateChange={value => value && setTempDateRange(prev => ({ ...prev, to: dayjs(value) }))}
                dateFormat="MM/DD/YYYY"
                filterDateFormat="MM/DD/YYYY"
                maxMonthsBack={240}
                allowSameDate={true}
                minDate={minDate}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setIsDatePickerOpen(false)}>Cancel</Button>
          <Button onClick={handleDateRangeConfirm} variant="contained" color="primary">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  )
}

export default DateRangePicker
